import { FC, useEffect, useState } from 'react';
import { useGetStoresListNoCacheQuery } from '@services';
import { Grid, ListItemButton, List, ListItemIcon, FormControl, TextField, Button, Typography } from '@mui/material';
import { LoadingSpinner } from '@components';
import { Edit } from '@mui/icons-material';
import { AvaDepotClustersEdit } from './AvaDepotClustersEdit';
import { generateNewClusterID } from './AvaDepotClusteringScreen.service';

type Props = {
  searchState: string;
};

interface Cluster {
  clusterID: string;
  clusterName: string;
  stores?: {
    id: string;
    name: string;
  }[];
}

export const AvaDepotClustersView: FC<Props> = ({ searchState }) => {
  const { data: stores, isLoading } = useGetStoresListNoCacheQuery();
  const storesInState = stores?.filter((store) => store.address?.state === searchState);
  const [selectedCluster, setSelectedCluster] = useState<Cluster | null>();
  const [isModalOpen, setModalOpen] = useState(false);
  const [newClusterID, setNewClusterID] = useState<string>('');
  const [newClusterName, setNewClusterName] = useState<string>('');
  const [storesByCluster, setStoresByCluster] = useState<Cluster[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const updatedStoresByCluster: any[] = [];

    setLoading(true);

    if (storesInState) {
      storesInState.forEach((store) => {
        if (store.cluster) {
          store.cluster.forEach((cluster) => {
            const existingClusterIndex = updatedStoresByCluster.findIndex((item) => item.clusterID === cluster.id);

            if (existingClusterIndex === -1) {
              updatedStoresByCluster.push({
                clusterName: cluster.name,
                clusterID: cluster.id,
                stores: [{ id: store.store_code || '0', name: store.name || 'N/A' }],
              });
            } else {
              updatedStoresByCluster[existingClusterIndex].stores.push({
                id: store.store_code || '0',
                name: store.name || 'N/A',
              });
            }
          });
        }
      });
    }

    setStoresByCluster(updatedStoresByCluster);
    setLoading(false);
  }, [searchState, stores]);

  const handleClusterClick = (clusterID: any, clusterName: any, stores?: any) => {
    setSelectedCluster({ clusterID, clusterName, stores });
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setSelectedCluster(null);
    setNewClusterName('');
    setLoading(false);
  };

  useEffect(() => {
    const newID = generateNewClusterID(storesByCluster, searchState);

    setNewClusterID(newID);
  }, [searchState, storesByCluster]);

  return isLoading || loading ? (
    <LoadingSpinner isOpen={isLoading || loading} />
  ) : (
    <>
      <Grid border="1px solid" data-testid="depot-cluster-view-grid" container item xs={12} p={2}>
        <Grid container item xs={6} spacing={2} p={2}>
          {storesByCluster.length !== 0 ? (
            <List
              dense
              sx={{ overflowY: 'scroll', border: '1px solid lightgrey', borderRadius: '4px', minWidth: '100%', maxHeight: '800px' }}
            >
              {storesByCluster.map((cluster) => (
                <ListItemButton
                  sx={{ justifyContent: 'space-between' }}
                  key={cluster.clusterID}
                  selected={selectedCluster != null && selectedCluster.clusterID === cluster.clusterID}
                  onClick={() => handleClusterClick(cluster.clusterID, cluster.clusterName, cluster.stores)}
                >
                  {cluster.clusterName}
                  <ListItemIcon>
                    <Edit />
                  </ListItemIcon>
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Typography variant="body1" p={2}>
              No clusters available for this state
            </Typography>
          )}
        </Grid>

        <Grid container item xs={6} spacing={2} p={2}>
          <FormControl onSubmit={() => handleClusterClick(newClusterID, newClusterName)} fullWidth>
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <TextField
                  name="2fa-code"
                  inputProps={{ 'data-testid': '2fa-input' }}
                  label="New Cluster Name"
                  placeholder="Enter a cluster name"
                  value={newClusterName}
                  onChange={(e) => setNewClusterName(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <Button
                  data-testid="add-a-new-cluster-btn"
                  variant="contained"
                  type="submit"
                  disabled={!newClusterName}
                  onClick={() => handleClusterClick(newClusterID, newClusterName)}
                  fullWidth
                >
                  Add a new cluster
                </Button>
              </Grid>
            </Grid>
          </FormControl>
        </Grid>
      </Grid>
      {selectedCluster != null && isModalOpen && storesInState && (
        <AvaDepotClustersEdit
          isOpen={isModalOpen}
          onClose={() => {
            handleClose();
          }}
          selectedCluster={selectedCluster}
          storesInState={storesInState}
        />
      )}
    </>
  );
};
